import { format } from "date-fns"
import { zonedTimeToUtc, utcToZonedTime, formatInTimeZone } from "date-fns-tz"

export const validateCurrentStep = (
  currentStep: number,
  description: string,
  fileUploadErrors: string[],
  isContactInfoValid: boolean | null,
  contactPreferences: string,
  showContactInfoForm: boolean,
  minDescriptionLength: number
): { isValid: boolean; errorMessage: string | null } => {
  if (currentStep === 1) {
    // Description step
    const isDescriptionValid = description.trim().length > minDescriptionLength
    const isFileUploadValid = fileUploadErrors.length === 0

    if (!isDescriptionValid) {
      return {
        isValid: false,
        errorMessage: `Please tell us a bit more about your issue (at least ${minDescriptionLength} characters needed).`,
      }
    }

    if (!isFileUploadValid) {
      return {
        isValid: false,
        errorMessage: "Something went wrong with your file upload, please try again.",
      }
    }

    return { isValid: true, errorMessage: null }
  }

  if (currentStep === 2) {
    // Contact preferences step
    if (showContactInfoForm && isContactInfoValid === false) {
      return {
        isValid: false,
        errorMessage:
          contactPreferences === "phone"
            ? "Please provide a valid phone number"
            : "Please provide a valid email address",
      }
    }
    return { isValid: true, errorMessage: null }
  }

  return { isValid: true, errorMessage: null }
}

const convertESTToLocalTime = (estTime: number): string | null => {
  try {
    const timeZoneEST = "America/New_York"
    const now = utcToZonedTime(new Date().toISOString(), "America/New_York")
    const year = formatInTimeZone(now, timeZoneEST, "yyyy")
    const month = formatInTimeZone(now, timeZoneEST, "MM")
    const day = formatInTimeZone(now, timeZoneEST, "dd")

    const estDateTimeString = `${year}-${month}-${day}T${String(estTime).padStart(2, "0")}:00:00`
    const utcDate = zonedTimeToUtc(estDateTimeString, timeZoneEST)
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const zonedLocalTime = utcToZonedTime(utcDate, localTimeZone)
    const formattedLocalTime = format(zonedLocalTime, "ha").toLowerCase() // e.g., "2pm"

    // Remove potential space in formats like "1 pm" -> "1pm"
    return formattedLocalTime.replace(" ", "")
  } catch (error) {
    console.error("[convertESTToLocalTime] Error during time conversion:", error)

    return null
  }
}

const formatEstHourFallback = (hour24: number): string => {
  const isPm = hour24 >= 12
  let hour12 = hour24 % 12
  if (hour12 === 0) {
    hour12 = 12
  }
  const amPm = isPm ? "pm" : "am"

  return `${hour12}${amPm}`
}

export const formatTimeRange = (startTimeEST: string, endTimeEST: string): string => {
  const HOURS_FORMAT = "{startTime}–{endTime}"
  const localStartTime = convertESTToLocalTime(parseInt(startTimeEST))
  const localEndTime = convertESTToLocalTime(parseInt(endTimeEST))

  if (localStartTime && localEndTime) {
    return HOURS_FORMAT.replace("{startTime}", localStartTime).replace("{endTime}", localEndTime)
  } else {
    // At least one conversion failed: Use fallback formatting
    const fallbackStartTime = formatEstHourFallback(parseInt(startTimeEST))
    const fallbackEndTime = formatEstHourFallback(parseInt(endTimeEST))

    return (
      HOURS_FORMAT.replace("{startTime}", fallbackStartTime).replace("{endTime}", fallbackEndTime) +
      " [EST]" // Keep the EST fallback indicator
    )
  }
}

export const isWeekend = (): boolean => {
  const date = new Date()
  const day = date.getDay()
  return day === 0 || day === 6
}
